import type { TreeNode, TreeNodeVersion, TreeNodeBranch, TreeNodeEmptyBranches } from './types';
import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { VersionRepresentationType } from '@readme/api/src/mappings/version/types';

import { useMemo, useState } from 'react';

import type { SuperHubStore } from '@core/store';

import { EMPTY_BRANCHES_ITEM_HEIGHT, BRANCH_ITEM_HEIGHT, VERSION_ITEM_HEIGHT } from './Item';

export interface useTreeNodesProps extends Pick<SuperHubStore['branches'], 'versionsMap'> {
  /**
   * Indicates that a parent version selected and should be used as the scroll
   * target to ensure it is visible in the virtual list.
   */
  selectedVersion?: string;

  /** Versions collection containing child branches. */
  versions: VersionRepresentationType[];
}

/**
 * Reduces the nested version + child branches structure into a flattened array
 * of tree nodes that can be used to render a virtual list window that is
 * capable of rendering very large data sets efficiently.
 * @example
 * ```ts
 * const [versions, versionsMap] = useSuperHubStore(s => [
 *   s.versions.data.data,
 *   s.branches.versionsMap,
 * ]);
 * const { nodes } = useTreeNodes({ versions, versionsMap });
 *
 * <VariableSizeList
 *   itemCount={nodes.length}
 *   itemData={nodes}
 *   itemSize={index => nodes[index].height}
 * >
 *   {Row}
 * </VariableSizeList>
 * ```
 */
export default function useTreeNodes({ selectedVersion, versions, versionsMap }: useTreeNodesProps) {
  const [isOpenMap, setIsOpenMap] = useState<Record<string, boolean>>({});

  const nodes = useMemo(() => {
    /**
     * References the tree node that should be made visible in the list view,
     * typically by scrolling the list to that item.
     */
    let scrollTarget: TreeNode | undefined;

    /** References the version node currently selected if one exists. */
    let selectedNode: TreeNodeVersion | undefined;

    /**
     * Global index counter incremented and assigned to every node in the tree.
     * Serves as the unique key to access any node by index in O(1).
     */
    let treeIndex = -1;

    /**
     * Returns an invocable function that's bounded to a node's index. When
     * invoked, the node at this slot has its "opened" state toggled.
     */
    function getToggleFn(uri: string, isOpen: boolean) {
      return () => {
        setIsOpenMap(value => ({ ...value, [uri]: !isOpen }));
      };
    }

    function getEmptyBranches() {
      treeIndex += 1;
      const node: TreeNodeEmptyBranches = {
        height: EMPTY_BRANCHES_ITEM_HEIGHT,
        index: treeIndex,
        type: 'empty_branches',
      };
      return [node];
    }

    function getBranch(
      branch: BranchRepresentationType,
      position: number,
      positionLength: number,
      parent: TreeNodeVersion,
    ) {
      treeIndex += 1;
      const node: TreeNodeBranch = {
        data: branch,
        height: BRANCH_ITEM_HEIGHT,
        index: treeIndex,
        nestingLevel: parent.nestingLevel + 1,
        position,
        positionLength,
        type: 'branch',
      };

      return [
        // Add child branch to tree.
        node,
      ];
    }

    function getChildBranches(parent: TreeNodeVersion): (TreeNodeBranch | TreeNodeEmptyBranches)[] {
      // When parent is closed, render nothing.
      if (!parent.isOpen) return [];

      const branchesData = versionsMap[parent.data.name];
      const branches = branchesData?.branches?.data;

      // While branches are being fetched, render nothing.
      if (!branches || branchesData?.isLoading) return [];

      // After being initialized with no branches, render empty state.
      if (!branches.length) {
        return getEmptyBranches();
      }

      return branches.flatMap((branch, index, thisArray) => {
        return getBranch(branch, index, thisArray.length, parent);
      });
    }

    function getVersionAndChildBranches(version: VersionRepresentationType, position: number, positionLength: number) {
      treeIndex += 1;
      const isOpen = isOpenMap[version.uri] ?? selectedVersion === version.name;
      const node: TreeNodeVersion = {
        branches: versionsMap[version.name]?.branches?.data || [],
        data: version,
        height: VERSION_ITEM_HEIGHT,
        index: treeIndex,
        isBranchesLoading: !!versionsMap[version.name]?.isLoading,
        isOpen,
        nestingLevel: 0,
        position,
        positionLength,
        toggleOpen: getToggleFn(version.uri, isOpen),
        type: 'version',
      };

      // Set the selected version as our scroll target to initialize to.
      if (version.name === selectedVersion) {
        selectedNode = node;
      }

      return [
        // Add parent version to tree.
        node,
        // Add child branches directly after their parent version.
        ...getChildBranches(node),
      ];
    }

    return {
      /** Flat list of all tree nodes. */
      nodes: versions.length
        ? [
            // Recursively add all versions and child branches.
            ...versions.flatMap((version, index, thisArray) => {
              return getVersionAndChildBranches(version, index, thisArray.length);
            }),
          ]
        : [],

      /**
       * References a tree node that should be scrolled into view by the virtual
       * list that is rendering the tree.
       * @example
       * ```ts
       * const listRef = useRef(null);
       * const { nodes, scrollTarget } = useTreeNodes(props);
       *
       * useEffect(() => {
       *   if (scrollTarget) {
       *     listRef.current?.scrollToItem(scrollTarget.index, 'auto');
       *   }
       * }, []);
       * ```
       */
      scrollTarget,

      /**
       * References the found version node based on `selectedVersion`. Returns
       * undefined if no matching version exists.
       */
      selectedNode,
    };
  }, [isOpenMap, selectedVersion, versions, versionsMap]);

  return nodes;
}
